import { Controller } from "@hotwired/stimulus"

// Fetches AI-generated captions from the server and lets the user pick one.
// Usage: data-controller="caption-generator"
//        data-caption-generator-url-value="/admin/generated_assets/:id/generate_caption"
//
// Targets:
//   button   — the "Generate AI caption" trigger button
//   textarea — the caption textarea to populate on selection
//   picker   — container div where caption option buttons are rendered
//   error    — element for error messages

export default class extends Controller {
  static targets = ["button", "textarea", "picker", "error"]
  static values  = {
    url:      String,
    platform: String,
    // Post-form mode: the asset isn't known when the page renders — it's
    // whichever mockup is first in the selection tray, and that changes as you
    // pick. urlTemplate carries a :id placeholder that assetId fills in.
    urlTemplate: String,
    assetId:     String,
    // A CSS selector for the element whose data-asset-id names the subject.
    // When set, the controller watches it and follows the selection.
    source:      String
  }

  connect() {
    // Fixed-subject mode (the run's schedule screen): the asset is known at
    // render time, so show any stored options straight away without calling
    // the model. A rewrite is only paid for when the button is pressed.
    if (!this.hasSourceValue) {
      if (this.hasUrlValue) this.generate({ cached: true })
      return
    }

    // A comma-separated list, searched in order — the post form has two places
    // a subject can come from (assets prefilled from a run, and the picker's
    // selection tray) and the first one that has something wins.
    this.sourceEls = this.sourceValue
      .split(",")
      .map(sel => document.querySelector(sel.trim()))
      .filter(Boolean)
    if (!this.sourceEls.length) return

    this.observer = new MutationObserver(() => this.syncFromSource())
    this.sourceEls.forEach(el => this.observer.observe(el, { childList: true, subtree: true }))
    this.syncFromSource()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // Follow the current selection: enable the button, and show any options
  // already stored for that asset without spending an API call.
  syncFromSource() {
    // Every selected asset, in order. The first drives the endpoint; the rest
    // ride along as `also[]` so a carousel is captioned as a set rather than
    // as its first slide.
    const rows = this.sourceEls.flatMap(el => Array.from(el.querySelectorAll("[data-asset-id]")))
    this.assetIds = rows.map(r => r.dataset.assetId).filter(Boolean)
    const assetId = this.assetIds[0] || ""

    if (assetId === this.assetIdValue) return
    this.assetIdValue = assetId
    this.clearState()

    if (this.hasButtonTarget) this.buttonTarget.disabled = !assetId
    if (assetId) this.generate({ cached: true })
  }

  // Resolves the endpoint, whether the asset was known at render time (schedule
  // screen) or is being tracked from the selection (post form).
  endpoint() {
    if (this.hasUrlTemplateValue) {
      if (!this.assetIdValue) return null
      return this.urlTemplateValue.replace(":id", this.assetIdValue)
    }
    return this.hasUrlValue ? this.urlValue : null
  }

  // `event` is present when called from data-action (Stimulus passes the click
  // event); an options object is passed when we call it ourselves.
  async generate(arg) {
    const cached = arg?.cached === true
    const path   = this.endpoint()
    if (!path) return

    // Two reads can be in flight at once: the automatic cached read fired when
    // the selection changes, and a real generation the user asked for. The
    // cached one is far faster, so it lands second in wall-clock terms often
    // enough to matter. Without a token the empty cached result would overwrite
    // (or error over) the real captions — which is exactly what produced a
    // spurious "No captions returned." next to a working generator.
    const token = (this._token = (this._token || 0) + 1)

    this.clearState()
    this.setLoading(!cached)

    try {
      const url = new URL(path, window.location.origin)
      if (this.hasPlatformValue) {
        url.searchParams.set('platform', this.platformValue)
      }
      // A cached read serves already-stored options and never calls the model.
      if (cached) url.searchParams.set('cached', '1')
      // Slides 2..n, so the caption covers the whole carousel.
      ;(this.assetIds || []).slice(1).forEach(id => url.searchParams.append('also[]', id))

      const response = await fetch(url, {
        headers: {
          "Accept":       "application/json",
          "X-CSRF-Token": this.csrfToken()
        }
      })

      const data = await response.json()
      if (token !== this._token) return   // superseded — drop it silently

      if (!response.ok || data.error) {
        this.showError(data.error || "Something went wrong.")
        return
      }

      // A cached read finding nothing is the ordinary first state, not a
      // failure: there is simply nothing stored for this asset yet.
      if (cached && !(data.captions || []).length) return

      this.renderPicker(data.captions)
    } catch (err) {
      if (token === this._token) this.showError("Network error — please try again.")
    } finally {
      if (token === this._token) this.setLoading(false)
    }
  }

  // Render option buttons for each returned caption
  renderPicker(captions) {
    if (!captions || captions.length === 0) {
      this.showError("The writer returned nothing — try again in a moment.")
      return
    }

    // captions may be a string (one block) or an array
    const list = Array.isArray(captions)
      ? captions
      : this.splitCaptions(captions)

    this.pickerTarget.innerHTML = ""

    list.forEach((caption, i) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.textContent = caption.trim()
      btn.className = [
        "w-full text-left text-xs px-3 py-2 rounded-lg border border-stone-200",
        "text-stone-600 hover:border-mauve-300 hover:text-mauve-700",
        "hover:bg-mauve-50 transition-colors leading-relaxed"
      ].join(" ")

      btn.addEventListener("click", () => {
        this.textareaTarget.value = caption.trim()
        // Highlight the selected button briefly
        btn.classList.add("border-mauve-400", "bg-mauve-50", "text-mauve-700")
        setTimeout(() => {
          btn.classList.remove("border-mauve-400", "bg-mauve-50", "text-mauve-700")
        }, 800)
      })

      this.pickerTarget.appendChild(btn)
    })

    this.pickerTarget.classList.remove("hidden")
  }

  // Split a single string response into numbered captions (1. ... 2. ... 3. ...)
  splitCaptions(text) {
    // Try splitting on numbered lines like "1.", "2.", "3."
    const parts = text.split(/\n?\d+\.\s+/).filter(s => s.trim().length > 0)
    return parts.length > 1 ? parts : [text]
  }

  setLoading(loading) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled  = loading
    this.buttonTarget.textContent = loading ? "Generating…" : "✨ Generate AI caption"
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearState() {
    if (this.hasPickerTarget) {
      this.pickerTarget.innerHTML = ""
      this.pickerTarget.classList.add("hidden")
    }
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
